import React from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';

// slice 02.1 · paso b.3 — presentacionales del picker de encargado/supervisor (eran los render
// helpers `renderUserCell` / `renderUserSelectPopover` de `ComponentTaskHoy`, usados en las
// filas de la lista y en el panel de detalle). `value` = el string `task.assignee`/`.supervisor`
// (nicks separados por coma). El contenedor mantiene el estado (qué celda está abierta, query,
// índice enfocado) y el `onToggleUser` con sus side-effects.

const avatarFor = (user, nick) =>
    (user?.['link-profile-img-thumb'] || user?.['link-profile-img'] || user?.image) ||
    `https://api.dicebear.com/7.x/avataaars/svg?seed=${nick}`;

export const UserCell = ({ value, systemUsers = [], onToggle }) => {
    const { t: translate } = useTranslation('tasks');
    const nicks = (value || '').split(',').map(s => s.trim()).filter(Boolean);

    const handleOpenPop = (e) => {
        e.stopPropagation();
        onToggle();
    };

    if (nicks.length === 0) {
        return (
            <div
                className="user-cell empty"
                onClick={handleOpenPop}
                onMouseDown={(e) => e.stopPropagation()}
                style={{ height: '14px', width: '30px', cursor: 'pointer' }}
            />
        );
    }

    if (nicks.length === 1) {
        const singleNick = nicks[0];
        const foundUser = systemUsers.find(u => u.nick.toLowerCase() === singleNick.toLowerCase());
        return (
            <div className="user-cell" onClick={handleOpenPop} onMouseDown={(e) => e.stopPropagation()}>
                <img src={avatarFor(foundUser, singleNick)} alt={singleNick} className="user-avatar" loading="lazy" />
                <span>{singleNick}</span>
            </div>
        );
    }

    // Stacking Multiple Avatars
    const stackedAvatars = nicks.map((nick, idx) => {
        const foundUser = systemUsers.find(u => u.nick.toLowerCase() === nick.toLowerCase());
        return (
            <img
                key={idx}
                src={avatarFor(foundUser, nick)}
                alt={nick}
                className="avatar-stack-img"
                style={{ zIndex: 10 - idx }}
                title={nick}
                loading="lazy"
            />
        );
    });

    return (
        <div className="user-cell" onClick={handleOpenPop}>
            <div className="avatar-stack" style={{ display: 'flex', alignItems: 'center' }}>
                {stackedAvatars.slice(0, 3)}
                {nicks.length > 3 && (
                    <span style={{ fontSize: '9px', color: '#666', marginLeft: 'var(--space-1)', display: 'flex', alignItems: 'center' }}>
                        +{nicks.length - 3}
                    </span>
                )}
            </div>
            <span className="avatar-stack-text" style={{ display: 'flex', alignItems: 'center' }}>{translate('common.team')}</span>
        </div>
    );
};

export const UserSelectPopover = ({ value, options = [], popoverRef, searchQuery, onSearchChange, focusedIdx, onFocusedIdxChange, onToggleUser }) => {
    const { t: translate } = useTranslation('tasks');
    const filteredOptions = options
        .filter(u => u.nick && u.nick.toLowerCase().includes(searchQuery.toLowerCase()))
        .slice(0, 4);
    const selectedNicks = (value || '').split(',').map(s => s.trim());

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            onFocusedIdxChange(prev => (filteredOptions.length > 0 ? (prev + 1) % filteredOptions.length : 0));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            onFocusedIdxChange(prev => (filteredOptions.length > 0 ? (prev - 1 + filteredOptions.length) % filteredOptions.length : 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filteredOptions.length > 0 && focusedIdx >= 0 && focusedIdx < filteredOptions.length) {
                onToggleUser(filteredOptions[focusedIdx].nick);
            }
        }
    };

    return (
        <div className="user-select-glass-popover" ref={popoverRef} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            <div className="user-select-search-container">
                <Search size={11} className="search-icon" />
                <input
                    type="text"
                    aria-label={translate('userSelect.searchAria')}
                    autoFocus
                    value={searchQuery}
                    onChange={(e) => {
                        onSearchChange(e.target.value);
                        onFocusedIdxChange(0);
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder={translate('userSelect.searchPlaceholder')}
                    className="user-select-search-input"
                />
            </div>
            <div className="user-select-options-list">
                {filteredOptions.length === 0 ? (
                    <div className="user-select-empty">{translate('empty.noResults')}</div>
                ) : (
                    filteredOptions.map((u, idx) => {
                        const isSelected = selectedNicks.includes(u.nick);
                        const isFocused = focusedIdx === idx;
                        return (
                            <div
                                key={u.id}
                                className={`user-select-option-item ${isFocused ? 'focused' : ''}`}
                                onClick={() => onToggleUser(u.nick)}
                                onMouseEnter={() => onFocusedIdxChange(idx)}
                            >
                                <img
                                    src={(u['link-profile-img-thumb'] || u['link-profile-img'] || u.image) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${u.nick}`}
                                    alt={u.nick}
                                    className="user-option-avatar"
                                    loading="lazy"
                                />
                                <span className="user-option-name">{u.nick}</span>
                                <div className={`user-option-toggle ${isSelected ? 'active' : ''}`}>
                                    <div className="toggle-handle" />
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};
